/**
 * L6 daily bounties (ประกาศล่า): the town board (at the daily-activity keeper, systems/dailyNpc.ts) posts 3 of the
 * region's bounties per real day (dayReset.ts, via `cultivation.boostDay`), picked by a pure hash of day + region.
 * Counts: a player kill of the target family (inside the pocket's box when it names one), or of the region's elite.
 * Pay on talking, scaled by the player's power rank: qi into the pool (systems/qiPool.ts) plus silver.
 */
import { BOUNTIES, BOUNTIES_PER_DAY, BOUNTY_PAY, type BountyDef } from '../data/bounties';
import { POCKETS } from '../data/layouts/plains';
import { regionOf, type RegionId } from '../data/regions';
import type { ZoneId } from '../data/zones';
import { rankFor } from '../cultivation';
import type { WorldEvent } from '../world';
import type { CultivationSystem } from './cultivation';
import type { ElitesSystem } from './elites';
import { dayRoll } from './dayRoll';
import type { QiPoolSystem } from './qiPool';
import type { SystemHost, WorldSystem } from './types';

/** The 3 bounties on a region's board on `day`: deterministic, no rng. */
export function bountiesFor(region: RegionId, day: number): BountyDef[] {
  const pool = BOUNTIES.filter((b) => b.region === region);
  const out: BountyDef[] = [];
  const s = (Math.imul(day | 0, 2654435761) ^ Math.imul(region.length * 97 + region.charCodeAt(0), 40503)) >>> 0;
  const start = (s >>> 8) % Math.max(1, pool.length);
  const step = pool.length > 2 && pool.length % 2 === 0 ? 1 + ((s >>> 4) % 2) * 0 : 1 + ((s >>> 4) % 2);
  for (let i = 0; i < Math.min(BOUNTIES_PER_DAY, pool.length); i++) out.push(pool[(start + i * step) % pool.length]!);
  return out;
}

/** Pay for one bounty at a power rank. */
export function bountyPay(b: BountyDef, rank: number): { qi: number; silver: number } {
  const m = b.kind === 'elite' ? BOUNTY_PAY.eliteMul : 1;
  const r = Math.max(0, rank);
  return {
    qi: Math.round((BOUNTY_PAY.qiBase + BOUNTY_PAY.qiPerRank * r) * m),
    silver: Math.round((BOUNTY_PAY.silverBase + BOUNTY_PAY.silverPerRank * r) * m),
  };
}

export class BountiesSystem implements WorldSystem {
  readonly name = 'bounties';
  day = Number.NaN;
  private count = new Map<string, number>();
  private paid = new Set<string>();
  constructor(private readonly h: SystemHost) {}

  private sys() {
    return (this.h.world as unknown as {
      sys: { cultivation: CultivationSystem; elites: ElitesSystem; qiPool: QiPoolSystem };
    }).sys;
  }
  private region(): RegionId {
    return regionOf(this.h.world.map.zone as ZoneId) ?? 'centre';
  }
  realDay(day: number): void {
    dayRoll(this, day, () => {
      this.count.clear();
      this.paid.clear();
    });
  }
  private roll(): number {
    const d = this.sys().cultivation.boostDay;
    dayRoll(this, d, () => {
      this.count.clear();
      this.paid.clear();
    });
    return d;
  }

  /** Today's board for this town's region. */
  board(): BountyDef[] {
    return bountiesFor(this.region(), this.roll());
  }
  progressOf(b: BountyDef): number {
    this.roll();
    return Math.min(b.n, this.count.get(b.id) ?? 0);
  }

  observe(e: WorldEvent): void {
    if (e.type !== 'reward') return;
    const w = this.h.world;
    if (w.auto.on) return;
    const m = w.entities.get(e.id);
    if (!m || !m.brain) return;
    const elite = this.sys().elites.isElite(m);
    this.roll();
    for (const b of this.board()) {
      if (b.monster !== m.brain.defId || (b.kind === 'elite' && !elite)) continue;
      if (b.pocket) {
        const box = POCKETS.find((p) => p.id === b.pocket)?.box;
        if (!box || m.x < box[0] || m.x >= box[1] || m.y < box[2] || m.y >= box[3]) continue;
      }
      this.count.set(b.id, (this.count.get(b.id) ?? 0) + 1);
    }
  }

  /** Pays every finished, unpaid bounty on the board; returns the Thai lines. */
  claim(): string[] {
    const rank = rankFor(this.h.world.progress.cultivationSpent).index;
    const lines: string[] = [];
    for (const b of this.board()) {
      if (this.paid.has(b.id) || this.progressOf(b) < b.n) continue;
      this.paid.add(b.id);
      const p = bountyPay(b, rank);
      this.sys().qiPool.add(p.qi);
      this.h.world.progress.gold += p.silver;
      lines.push(`ได้รับรางวัลประกาศล่า: ${p.qi} ลมปราณ · ${p.silver} ตำลึง`);
    }
    return lines;
  }

  lines(): string[] {
    this.roll();
    return [
      '• ประกาศล่าวันนี้:',
      ...this.board().map((b) => {
        const what = b.kind === 'elite' ? `ปราบหัวหน้า ${b.monster} ที่ ${b.where}` : `ปราบ ${b.monster} ×${b.n} ที่ ${b.where}`;
        return `   ${this.paid.has(b.id) ? '✓' : this.progressOf(b) >= b.n ? '◎' : '○'} ${what} (${this.progressOf(b)}/${b.n})`;
      }),
    ];
  }
}
